import React, { useEffect, useId, useState } from 'react';
import { SAMPLES, SAMPLE_NOTE } from '../lib/samples';
import { CodePanel } from './Statement';
import Icon from './Icon';

const reducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Counts a figure in from zero, keeping its prefix, separators and suffix. The final value is
// what's rendered until animation starts, and the only value with reduced motion.
function CountFigure({ value }) {
  const [shown, setShown] = useState(value);

  useEffect(() => {
    const match = value.match(/^(\D*)([\d,]+)(.*)$/);
    if (!match || reducedMotion()) {
      setShown(value);
      return undefined;
    }
    const [, pre, digits, post] = match;
    const target = Number(digits.replace(/,/g, ''));
    const start = performance.now();
    let frame;
    const tick = (now) => {
      const t = Math.min((now - start) / 650, 1);
      const eased = 1 - (1 - t) ** 3;
      setShown(`${pre}${Math.round(target * eased).toLocaleString('en-US')}${post}`);
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    // Background tabs pause rAF: never leave a part-counted figure on screen.
    const done = setTimeout(() => setShown(value), 900);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(done);
    };
  }, [value]);

  return <span aria-hidden="true">{shown}</span>;
}

function Figure({ value }) {
  return (
    <>
      <span className="sr-only">{value}</span>
      <CountFigure value={value} />
    </>
  );
}

function Working({ sample, id }) {
  return (
    <div id={id} className="swap-in border-t border-stone">
      <CodePanel title="The calculation" code={sample.sql} language="sql" maxHeight="190px" />
      <dl className="m-0">
        {[
          ['The data used', sample.data],
          ['Rows sent back', sample.rowsBack],
          ['Rows copied', '0'],
          ['Reference to quote', sample.reference],
        ].map(([k, v]) => (
          <div key={k} className="statement-row px-5 first:border-t-0 sm:px-7">
            <dt className="text-glass">{k}</dt>
            <dd className={`m-0 text-right figure ${k === 'Reference to quote' || k === 'The data used' ? 'font-mono text-[13px]' : ''}`}>
              {v}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Answer({ sample, compact }) {
  return (
    <div key={sample.id} className="swap-in">
      <p
        aria-live="polite"
        className={`px-5 pb-5 pt-5 font-display sm:px-7 ${
          compact ? 'text-[20px] leading-[1.32]' : 'text-[clamp(22px,2vw,30px)] leading-[1.28]'
        }`}
      >
        {sample.a}
      </p>
      <div>
        <div className="flex items-baseline justify-between border-t border-wall/70 px-5 py-2 sm:px-7">
          <span className="label text-glass">{sample.figuresLabel}</span>
          <span className="label text-glass">Sample</span>
        </div>
        {sample.figures.map((f) => (
          <div key={f.label} className="statement-row px-5 sm:px-7">
            <span>{f.label}</span>
            <span className={`figure whitespace-nowrap font-medium ${f.tone === 'over' ? 'text-oxide' : ''}`}>
              {f.note && <span className="mr-3 font-normal text-glass">{f.note}</span>}
              <Figure value={f.value} />
            </span>
          </div>
        ))}
        {sample.total && (
          <div className="statement-row border-t-wall/70 px-5 font-semibold sm:px-7">
            <span>{sample.total.label}</span>
            <span className="figure whitespace-nowrap">
              {sample.total.note && <span className="mr-3 font-normal text-glass">{sample.total.note}</span>}
              <Figure value={sample.total.value} />
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// The live Answer Receipt. variant="hero" is the compact panel crossing the home hero's seam;
// variant="wide" sets the question list beside the answer, with the working open.
function AnswerReceipt({ variant = 'hero', className = '' }) {
  const compact = variant === 'hero';
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(!compact);
  const workingId = useId();
  const sample = SAMPLES[active];

  const toggle = (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={workingId}
      onClick={() => setOpen((v) => !v)}
      className="flex min-h-[48px] w-full items-center justify-between gap-4 border-t border-wall/70 px-5 py-3 text-left font-semibold transition-colors hover:bg-limestone sm:px-7"
    >
      How this was worked out
      <Icon name={open ? 'minus' : 'plus'} size={18} />
    </button>
  );

  const picker = (
    <div role="group" aria-label="Sample questions">
      {SAMPLES.map((s, i) => {
        const on = i === active;
        return (
          <button
            key={s.id}
            type="button"
            aria-pressed={on}
            onClick={() => setActive(i)}
            className={`group relative flex min-h-[48px] w-full items-center gap-3 border-t border-stone px-5 text-left transition-colors first:border-t-0 sm:px-7 ${
              compact ? 'py-2.5 text-[15px] leading-snug' : 'py-5 font-display text-[clamp(20px,1.7vw,25px)] leading-[1.25]'
            } ${on ? 'bg-limestone text-wall' : 'text-glass hover:bg-limestone/60 hover:text-wall'}`}
          >
            <span
              aria-hidden="true"
              className={`absolute inset-y-0 left-0 w-[3px] transition-colors ${on ? 'bg-bronze' : 'bg-transparent'}`}
            />
            {s.q}
          </button>
        );
      })}
    </div>
  );

  const head = (
    <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 px-5 pb-3 pt-4 sm:px-7">
      <span className="label">Answer receipt</span>
      <span className="text-[13px] text-glass figure">
        {sample.rowsBack} · {sample.time}
      </span>
    </div>
  );

  const note = <p className="border-t border-stone px-5 py-3 text-[13px] font-medium text-glass sm:px-7">{SAMPLE_NOTE}</p>;

  if (compact) {
    return (
      <aside aria-label="Answer receipt example" className={`statement shadow-raised ${className}`}>
        {head}
        <div className="border-t border-wall/70">{picker}</div>
        <div className="border-t border-wall/70">
          <Answer sample={sample} compact />
        </div>
        {toggle}
        {open && <Working sample={sample} id={workingId} />}
        {note}
      </aside>
    );
  }

  return (
    <div className={`statement grid grid-cols-1 shadow-panel lg:grid-cols-12 ${className}`}>
      <div className="lg:col-span-5 lg:border-r lg:border-stone">
        <div className="px-5 pb-3 pt-4 sm:px-7">
          <span className="label">Pick a question</span>
        </div>
        <div className="border-t border-wall/70">{picker}</div>
        {note}
      </div>
      <div className="min-w-0 border-t border-wall/70 lg:col-span-7 lg:border-t-0">
        {head}
        <div className="border-t border-wall/70">
          <Answer sample={sample} />
        </div>
        {toggle}
        {open && <Working sample={sample} id={workingId} />}
      </div>
    </div>
  );
}

export default AnswerReceipt;
